import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  GET_CLIENTS_BY_CUSTOMER,
  GET_GROUPS_BY_CLIENT,
} from "../../common/operations/queries";
import { useCustomerIdGuard } from "../../common/hooks/useCustomerIdGuard";
import client from "../../configs/apolloClient";
import { errorNotification } from "../../common/variables/notification";
import { PaginatedClients, PaginatedGroups } from "../../API";

export const useGetPaginatedClientsAndGroups = () => {
  const { state } = useLocation();
  const routeCustomerId = state?.customerId;
  const customerId = routeCustomerId || useCustomerIdGuard();

  const [clients, setClients] = useState<PaginatedClients | null>(null);
  const [groupsMap, setGroupsMap] = useState<Record<string, PaginatedGroups>>(
    {}
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;

    let canceled = false;

    setLoading(true);

    client
      .query<{ getClientsByCustomer: PaginatedClients }>({
        query: GET_CLIENTS_BY_CUSTOMER,
        variables: { input: { customerId } },
      })
      .then(({ data }) => {
        if (canceled) return;

        if (data.getClientsByCustomer?.items?.length === 0) {
          setLoading(false);

          setClients(null);

          return;
        }

        setClients(data.getClientsByCustomer);
      })
      .catch(async err => {
        console.error("Error fetching clients:", err);

        await client.cache.reset();

        errorNotification();
      });

    return () => {
      canceled = true;
    };
  }, [customerId]);

  const fetchGroups = useCallback(
    async (clientIds: string[]) => {
      if (!clientIds.length || !customerId) return;

      setLoading(true);
      try {
        const queries = clientIds.map(id =>
          client.query<{ getGroupsByClient: PaginatedGroups }>({
            query: GET_GROUPS_BY_CLIENT,
            variables: { input: { clientId: id, customerId } },
            fetchPolicy: "network-only",
          })
        );
        const results = await Promise.all(queries);

        const newMap: Record<string, PaginatedGroups> = {};

        results.forEach((res, idx) => {
          const id = clientIds[idx];

          if (res.data.getGroupsByClient) {
            newMap[id] = res.data.getGroupsByClient;
          }
        });

        setGroupsMap(newMap);
      } catch (err) {
        console.error("Error fetching groups:", err);

        await client.cache.reset();

        errorNotification();
      } finally {
        setLoading(false);
      }
    },
    [customerId]
  );

  useEffect(() => {
    if (clients?.items?.length) {
      const ids = clients.items.map(c => c.id);

      fetchGroups(ids);
    }
  }, [clients, customerId]);

  const deleteGroup = useCallback((clientId: string, groupId: string) => {
    setGroupsMap(prev => ({
      ...prev,
      [clientId]: {
        ...prev[clientId],
        items: prev[clientId].items.filter(g => g.id !== groupId),
      },
    }));
  }, []);

  const groups = useMemo<PaginatedGroups[]>(() => {
    return Object.values(groupsMap);
  }, [groupsMap]);

  return {
    clients,
    groups,
    deleteGroup,
    pageLoading: loading,
  };
};
